import { Notice } from '@verlo/shared/components/ui/notice';
import { useSite, useHasTill } from '../../contexts/SiteContext';

/**
 * The one page a back-office site sees where the till pages would be ("The restaurant" group in
 * `areas.js`). It exists so the front of house is discoverable from inside the product
 * rather than only from a sales call — said once, in the gap the hidden pages leave,
 * instead of scattered as locked rows across every report.
 *
 * A site that already runs Verlo on the floor never sees the nav entry; if it lands here
 * by a stale bookmark the page says so rather than pitching it something it has.
 */
export default function FrontOfHousePage() {
  const { current, sites } = useSite();
  // `useHasTill`, not a second copy of its rule. This page had its own — `current` alone,
  // defaulting the other way — so it answered the same question differently from the nav
  // that brings people here: a one-site venue has no `current` until somebody picks a site
  // in the header, which below two sites the header gives them no way to do.
  const hasTill = useHasTill();
  // The venue being addressed, resolved the way the hook resolves it, so the page can name
  // them rather than saying "This venue" to a customer with exactly one.
  const venue = current || ((sites || []).length === 1 ? sites[0] : null);
  const name = venue?.name || 'This venue';

  if (hasTill) {
    return (
      <div className="mx-auto max-w-prose px-4 py-10">
        <Notice tone="ok" word="Front of house" title={`${name} already runs service on Verlo`}>
          Live service, the cash-ups and the till health are all in Reports.
        </Notice>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-prose px-4 py-10 space-y-6">
      <div className="space-y-2">
        <div className="font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
          Front of house
        </div>
        <h1 className="font-display text-[28px] leading-tight text-ink">
          Run service on Verlo too
        </h1>
        <p className="text-ink-soft">
          {name} uses Verlo for the back office: the menu, the suppliers, the reports built
          from the sales you send us. Service itself still runs on your current till.
        </p>
      </div>

      <div className="space-y-4 border-t border-hairline pt-6">
        <h2 className="font-heading text-[15px] font-bold text-ink">What changes</h2>
        <ul className="space-y-3 text-ink-soft">
          <li>
            <span className="font-semibold text-ink">The numbers arrive as they happen.</span>{' '}
            Today&rsquo;s reports are as fresh as your last upload. With Verlo on the floor
            they are as fresh as the last order.
          </li>
          <li>
            <span className="font-semibold text-ink">Live service, cash-ups and till health.</span>{' '}
            The screens hidden from this nav fill in from the first service, with no setup:
            the menu and the team are already here.
          </li>
          <li>
            <span className="font-semibold text-ink">One menu, not two.</span>{' '}
            The menu you keep in Verlo becomes the menu the till sells, so a price change
            stops being a job done twice.
          </li>
        </ul>
      </div>

      <div className="space-y-3 border-t border-hairline pt-6">
        <h2 className="font-heading text-[15px] font-bold text-ink">When</h2>
        <p className="text-ink-soft">
          Most venues switch when their till contract comes up for renewal. We can usually
          keep the hardware you already have. Tell us the renewal date and we will plan the
          changeover around a quiet service.
        </p>
      </div>
    </div>
  );
}
